import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/useAuthStore";
import { useFormStore } from "../../store/useFormStore";
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  Plug,
  Palette,
  Shield,
  UserCog,
  Building2,
  FileText,
  BookOpen,
  Radio,
  Workflow,
  Database,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Moon,
  Sun,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useThemeStore } from "../../store/useThemeStore";

const NAV_GROUPS = [
  {
    label: "Operacional",
    items: [
      { label: "Dashboard", icon: LayoutDashboard, to: "/admin", end: true },
      { label: "Substituídos", icon: Users, to: "/admin/cedentes" },
      { label: "Gestão", icon: FolderKanban, to: "/admin/management" },
    ],
  },
  {
    label: "Configurações",
    adminOnly: true,
    items: [
      { label: "Config. Assinatura", icon: Plug, to: "/admin/assinaturas" },
      { label: "Tema", icon: Palette, to: "/admin/tema" },
      { label: "Sindicatos", icon: Building2, to: "/admin/sindicatos" },
    ],
  },
  {
    label: "Sistema",
    adminOnly: true,
    items: [
      { label: "Perfis", icon: Shield, to: "/admin/roles" },
      { label: "Usuários", icon: UserCog, to: "/admin/usuarios" },
      { label: "Tipos Doc.", icon: FileText, to: "/admin/document-types" },
    ],
  },
  {
    label: "Integrações",
    adminOnly: true,
    items: [
      { label: "Webhooks", icon: Radio, to: "/admin/webhooks" },
      { label: "CRM", icon: Workflow, to: "/admin/crm" },
      { label: "Conexões", icon: Database, to: "/admin/data-connections" },
      { label: "Docs", icon: BookOpen, to: "/admin/docs" },
    ],
  },
];

function SidebarContent({ onNavigate }) {
  const navigate = useNavigate();
  const { logout, permissions } = useAuthStore();
  const { clearForm } = useFormStore();
  const { theme } = useThemeStore();
  const logoSrc = theme.logo_url || "/logo.png";

  const handleLogout = () => {
    clearForm();
    logout();
    navigate("/login");
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="sidebar-divider px-5 py-5 border-b">
        <img src={logoSrc} alt="Logo" className="h-10 object-contain" />
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {NAV_GROUPS.filter(({ adminOnly }) => !adminOnly || permissions.includes('usuarios')).map(({ label, items }, groupIndex) => (
          <div key={label}>
            {groupIndex > 0 && (
              <span className="block text-[10px] font-semibold px-3 pt-3 pb-1 tracking-widest uppercase" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {label}
              </span>
            )}
            <div className="space-y-1">
              {items.map(({ label: itemLabel, icon: Icon, to, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn("sidebar-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium", isActive && "active")
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="flex-1">{itemLabel}</span>
                      {isActive && <ChevronRight className="w-3 h-3 opacity-60" />}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Logout */}
      <div className="sidebar-divider px-3 py-4 border-t">
        <button
          onClick={handleLogout}
          className="sidebar-item flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium"
        >
          <LogOut className="w-4 h-4 shrink-0" />
          Sair
        </button>
      </div>
    </div>
  );
}

function contrastColor(hex) {
  if (!hex || hex.length < 4) return '#111827';
  const full = hex.length === 4
    ? '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3]
    : hex;
  const r = parseInt(full.slice(1, 3), 16);
  const g = parseInt(full.slice(3, 5), 16);
  const b = parseInt(full.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? '#111827' : '#ffffff';
}

export function AdminLayout({ children, pageTitle }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains('dark')
  );
  const { theme, toggleDarkMode } = useThemeStore();
  const headerBg = isDark ? '#24261F' : (theme?.header_bg || '#ffffff');
  const headerFg = isDark ? '#E8E6E1' : contrastColor(theme?.header_bg || '#ffffff');

  const handleToggleDark = () => {
    toggleDarkMode();
    setIsDark(d => !d);
  };

  return (
    <div data-page className="min-h-screen flex">
      {/* Desktop Sidebar */}
      <aside
        data-sidebar
        className="hidden md:flex w-56 shrink-0 flex-col fixed inset-y-0 left-0 z-30"
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside
        data-sidebar
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-56 flex flex-col transition-transform duration-300 md:hidden",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <button
          onClick={() => setSidebarOpen(false)}
          className="absolute top-4 right-4 text-white/60 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        <SidebarContent onNavigate={() => setSidebarOpen(false)} />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col md:ml-56 min-w-0">
        {/* Topbar */}
        <header
          data-header
          className="border-b shadow-sm sticky top-0 z-20"
          style={{ backgroundColor: headerBg, borderColor: `${headerFg}26` }}
        >
          <div className="flex items-center gap-4 px-4 md:px-6 h-14">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden"
              style={{ color: `${headerFg}99` }}
            >
              <Menu className="w-5 h-5" />
            </button>
            {pageTitle && (
              <h1 className="text-base font-semibold truncate" style={{ color: headerFg }}>
                {pageTitle}
              </h1>
            )}
            <div className="ml-auto">
              <button
                onClick={handleToggleDark}
                title={isDark ? "Modo claro" : "Modo escuro"}
                className="p-2 rounded-lg hover:bg-black/10 transition-colors"
                style={{ color: `${headerFg}99` }}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
